import { ref } from "vue";
import { fetchProfile, getAccessToken } from "./services/api.js";
import { localeLabels } from "./i18n/languageMetadata.js";

const LOCALE_STORAGE_KEY = "wishlist_locale";
const LOCALE_QUERY_PARAM = import.meta.env.VITE_LOCALE_QUERY_PARAM || "lang";
const localeModuleLoaders = import.meta.glob("./locales/*.mjs");
const localeLoaders = Object.fromEntries(
  Object.entries(localeModuleLoaders).map(([modulePath, moduleLoader]) => {
    const localeCode = modulePath.replace("./locales/", "").replace(".mjs", "");
    return [localeCode, moduleLoader];
  }),
);

const SUPPORTED_LOCALES = Object.keys(localeLoaders).sort();

const configuredDefaultLocale =
  normalizeSupportedLocale(import.meta.env.VITE_DEFAULT_LOCALE)
  || normalizeSupportedLocale("en")
  || SUPPORTED_LOCALES[0];

export const messages = {};
const loadedLocales = new Set();
const currentLocale = ref(configuredDefaultLocale);

/**
 * Check whether one locale code is supported.
 *
 * @param {string} locale Locale code
 * @returns {boolean} true when locale is supported
 */
export function isSupportedLocale(locale) {
  return Boolean(normalizeSupportedLocale(locale));
}

/**
 * Return all supported locale codes.
 *
 * @returns {string[]} locale list
 */
export function getSupportedLocales() {
  return [...SUPPORTED_LOCALES];
}

/**
 * Return the localized label for one supported locale.
 *
 * @param {string} localeCode Locale code to label
 * @returns {string} native label or a fallback code
 */
export function getLocaleLabel(localeCode) {
  const normalizedLocale = normalizeSupportedLocale(localeCode);
  if (!normalizedLocale) {
    return String(localeCode || "").toUpperCase();
  }

  return localeLabels[normalizedLocale] || normalizedLocale.toUpperCase();
}

/**
 * Return the currently active locale code.
 *
 * @returns {string} active locale
 */
export function getCurrentLocale() {
  return currentLocale.value;
}

/**
 * Load locale messages lazily when needed.
 *
 * @param {string} locale Locale code
 */
async function ensureLocaleMessages(locale) {
  const normalizedLocale = normalizeSupportedLocale(locale);
  if (!normalizedLocale || loadedLocales.has(normalizedLocale)) {
    return;
  }

  const localeLoader = localeLoaders[normalizedLocale];
  if (!localeLoader) {
    return;
  }

  const localeModule = await localeLoader();
  const localeMessages = localeModule?.default;
  if (localeMessages && typeof localeMessages === "object" && !Array.isArray(localeMessages)) {
    messages[normalizedLocale] = localeMessages;
    loadedLocales.add(normalizedLocale);
  }
}

/**
 * Set the active locale and persist the choice by default.
 *
 * @param {string} locale Locale code
 * @param {Object} [options] set locale options
 * @param {boolean} [options.persist=true] persist locale in local storage
 * @returns {Promise<string>} resolved active locale code
 */
export async function setLocale(locale, options = {}) {
  const persist = options.persist !== false;
  const normalizedLocale = normalizeSupportedLocale(locale) || configuredDefaultLocale;

  try {
    await ensureLocaleMessages(normalizedLocale);
    currentLocale.value = normalizedLocale;
  } catch (error) {
    currentLocale.value = configuredDefaultLocale;
    await ensureLocaleMessages(configuredDefaultLocale);
  }

  if (persist) {
    saveStoredLocale(currentLocale.value);
  }

  return currentLocale.value;
}

/**
 * Initialize locale from profile, storage, request hints, and default env.
 *
 * Resolution order:
 * 1. Profile language, if available and supported.
 * 2. Stored language in local storage.
 * 3. Requested language from query parameter or browser.
 * 4. Default locale from environment.
 *
 * @returns {Promise<string>} initialized locale code
 */
export async function initializeLocale() {
  const resolvedLocale = await resolveInitialLocale();
  await setLocale(resolvedLocale, { persist: false });
  saveStoredLocale(currentLocale.value);
  return currentLocale.value;
}

/**
 * Translate a key into the chosen locale.
 *
 * @param {string} key A dotted path into the messages object
 * @param {string|Object} [locale] Optional locale, defaults to current locale
 * @returns {string}
 */
export function t(key, locale = undefined, params = {}) {
  let resolvedLocale = typeof locale === "string" ? locale : currentLocale.value;
  if (locale && typeof locale === "object" && !Array.isArray(locale)) {
    params = locale;
    resolvedLocale = currentLocale.value;
  }
  if (typeof params !== "object" || params === null || Array.isArray(params)) {
    params = {};
  }

  const normalizedLocale = normalizeSupportedLocale(resolvedLocale) || currentLocale.value;
  const current = resolveMessageKey(key, normalizedLocale);
  if (typeof current !== "string") {
    return key;
  }
  return interpolateMessage(current, params);
}

/**
 * Translate a key with locale aware pluralization.
 *
 * @param {string} key A dotted path into the messages object
 * @param {number} count The quantity used for plural rule selection
 * @param {string|Object} [locale] Optional locale, defaults to current locale
 * @param {Object} [params] Optional interpolation params
 * @returns {string}
 */
export function tp(key, count, locale = undefined, params = {}) {
  let resolvedLocale = typeof locale === "string" ? locale : currentLocale.value;
  if (locale && typeof locale === "object" && !Array.isArray(locale)) {
    params = locale;
    resolvedLocale = currentLocale.value;
  }
  if (typeof params !== "object" || params === null || Array.isArray(params)) {
    params = {};
  }

  const normalizedLocale = normalizeSupportedLocale(resolvedLocale) || currentLocale.value;
  const pluralMessages = resolveMessageKey(key, normalizedLocale);
  if (!pluralMessages || typeof pluralMessages !== "object" || Array.isArray(pluralMessages)) {
    return key;
  }

  const pluralRules = new Intl.PluralRules(normalizedLocale);
  const pluralCategory = pluralRules.select(Number(count));
  const template =
    pluralMessages[pluralCategory]
    || pluralMessages.other
    || pluralMessages.one;

  if (typeof template !== "string") {
    return key;
  }

  return interpolateMessage(template, { ...params, count: Number(count) });
}

/**
 * Resolve initial locale according to priority order.
 *
 * @returns {Promise<string>} locale code
 */
async function resolveInitialLocale() {
  const profileLocale = await resolveProfileLocale();
  if (profileLocale) {
    return profileLocale;
  }

  const storedLocale = resolveStoredLocale();
  if (storedLocale) {
    return storedLocale;
  }

  const requestedLocale = resolveRequestedLocale();
  if (requestedLocale) {
    return requestedLocale;
  }

  return configuredDefaultLocale;
}

/**
 * Resolve locale from current authenticated profile.
 *
 * @returns {Promise<string|null>} locale code or null
 */
async function resolveProfileLocale() {
  if (!getAccessToken()) {
    return null;
  }

  try {
    const profile = await fetchProfile();
    return normalizeSupportedLocale(profile?.language);
  } catch (error) {
    return null;
  }
}

/**
 * Resolve locale from local storage.
 *
 * @returns {string|null} locale code or null
 */
function resolveStoredLocale() {
  try {
    const storedLocale = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    return normalizeSupportedLocale(storedLocale);
  } catch (error) {
    return null;
  }
}

/**
 * Resolve locale from URL query parameter then browser languages.
 *
 * @returns {string|null} locale code or null
 */
function resolveRequestedLocale() {
  const queryLocale = resolveLocaleFromQuery();
  if (queryLocale) {
    return queryLocale;
  }

  const browserLocales = [];
  if (Array.isArray(window.navigator.languages)) {
    browserLocales.push(...window.navigator.languages);
  }
  if (window.navigator.language) {
    browserLocales.push(window.navigator.language);
  }

  for (const candidate of browserLocales) {
    const normalized = normalizeSupportedLocale(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return null;
}

/**
 * Resolve locale from query parameter.
 *
 * @returns {string|null} locale code or null
 */
function resolveLocaleFromQuery() {
  try {
    const params = new URLSearchParams(window.location.search || "");
    const requested = params.get(LOCALE_QUERY_PARAM);
    return normalizeSupportedLocale(requested);
  } catch (error) {
    return null;
  }
}

/**
 * Normalize incoming locale and match it against supported locales.
 *
 * @param {string} locale locale candidate
 * @returns {string|null} matched locale code
 */
function normalizeSupportedLocale(locale) {
  if (!locale || typeof locale !== "string") {
    return null;
  }

  const normalizedLocale = locale.trim().toLowerCase().replace("_", "-");
  if (!normalizedLocale) {
    return null;
  }

  if (SUPPORTED_LOCALES.includes(normalizedLocale)) {
    return normalizedLocale;
  }

  const rootLocale = normalizedLocale.split("-")[0];
  if (SUPPORTED_LOCALES.includes(rootLocale)) {
    return rootLocale;
  }

  return null;
}

/**
 * Persist locale safely in local storage.
 *
 * @param {string} locale locale code
 */
function saveStoredLocale(locale) {
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch (error) {
    // Ignore storage write failures in restricted environments.
  }
}

/**
 * Resolve one translation entry from a dotted key path.
 *
 * @param {string} key Translation dotted key path
 * @param {string} locale Locale code
 * @returns {string|Object} translation entry or key when missing
 */
function resolveMessageKey(key, locale) {
  const normalizedLocale = normalizeSupportedLocale(locale);
  if (!normalizedLocale) {
    return key;
  }
  return resolveMessageKeyForLocale(key, normalizedLocale) ?? key;
}

/**
 * Resolve one translation key for one specific locale.
 *
 * @param {string} key Translation dotted key path
 * @param {string} locale Locale code
 * @returns {string|Object|undefined} translation value or undefined
 */
function resolveMessageKeyForLocale(key, locale) {
  const segments = key.split(".");
  let current = messages[locale];

  for (const segment of segments) {
    if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
      current = current[segment];
    } else {
      return undefined;
    }
  }

  return current;
}

/**
 * Interpolate placeholders in a translation template.
 *
 * @param {string} template Translation string template
 * @param {Object} params Placeholder values
 * @returns {string} interpolated string
 */
function interpolateMessage(template, params) {
  return template.replace(/\{(\w+)\}/g, (match, token) => {
    if (Object.prototype.hasOwnProperty.call(params, token)) {
      return `${params[token]}`;
    }
    return match;
  });
}
